import { apiCustomer } from "@/api"
import {
  isScheduledCancellationRefusal,
  translateScheduledCancellationRefusal,
  scheduledCancellationFailureMessage,
  type ScheduledCancellationRefusal,
} from "@/lib/i18n/error-messages"
import type { OrderResponse } from "@/types/external"

/**
 * Outcome of `POST /customers/orders/:id/cancel`. Refusal and hard failure are
 * separate arms because only the refusal is actionable copy from the contract
 * — a 500 or a dropped connection must not be reported as "too late".
 */
export type CancelScheduledOrderResult =
  | { outcome: "cancelled"; order: OrderResponse | null }
  | { outcome: "refused"; refusal: ScheduledCancellationRefusal | null; message: string }
  | { outcome: "failed"; message: string }

type ConflictBody = {
  code?: unknown
  refusal?: unknown
  message?: unknown
}

type HttpErrorShape = {
  response?: { status?: number; data?: unknown }
}

function readConflict(error: unknown): ConflictBody | null {
  const response = (error as HttpErrorShape | null)?.response
  if (!response || response.status !== 409) return null
  const data = response.data
  return data !== null && typeof data === "object" ? (data as ConflictBody) : {}
}

/**
 * Cancels a scheduled order on behalf of the signed-in customer. `customer_id`
 * is never sent: the gateway takes it from the Bearer subject, and `apiCustomer`
 * attaches that token.
 */
export async function cancelScheduledOrder(
  orderId: string,
): Promise<CancelScheduledOrderResult> {
  try {
    const { data } = await apiCustomer.post<OrderResponse>(`/orders/${orderId}/cancel`)
    return { outcome: "cancelled", order: data ?? null }
  } catch (error) {
    const conflict = readConflict(error)
    if (!conflict) return { outcome: "failed", message: scheduledCancellationFailureMessage() }
    const refusal = isScheduledCancellationRefusal(conflict.refusal) ? conflict.refusal : null
    return {
      outcome: "refused",
      refusal,
      message: translateScheduledCancellationRefusal(conflict.refusal),
    }
  }
}
